import { Dimmer, Grid, Loader, Modal } from 'semantic-ui-react';
import { TenantDto } from "ts-types/api.types";
import axios, { CancelTokenSource } from 'axios';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import Input from 'components/final-form/Input';
import { debounce } from "ts-debounce";
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { deleteTenant, findTenantById, searchTenants } from 'services/tenantServices';
import { noop } from 'utils/functionUtils';
import DeleteActionButton from 'components/DeleteActionButton';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PxGrid from 'components/layout/PxGrid';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import UpsertTenantForm from 'components/user-settings/UpsertTenantForm';
import { FormState } from 'final-form';
import EditActionButton from 'components/EditActionButton';
import AddButton from 'components/AddButton';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import SearchResultComponent from 'components/SearchResultComponent';

interface TenantFieldProps extends FormState {
  tenantSearchTerm?: string
}

interface Props extends MessageSourceProps {
}

interface State {
  foundTenants: Array<TenantDto>,
  tenantsLoaded: boolean,
  tenant?: TenantDto,
  tenantId?: string,
  addEditModalOpen: boolean,
  loading: boolean,
  searchValues: string,
  cancelTokenSource: CancelTokenSource
  isDefault: boolean,
}

class TenantView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundTenants: [],
      tenantsLoaded: false,
      addEditModalOpen: false,
      loading: false,
      searchValues: "",
      isDefault: false,
      cancelTokenSource
    }

  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  openAddEditModal = (id?: string) => {
    const {cancelTokenSource} = this.state;

    if (id) {
      findTenantById(id, cancelTokenSource).then(response => {
        this.showModal(id, response);
      }).catch(noop);
    }
  };

  showModal = (id?: string, tenant?: TenantDto) => {

    this.setState({
      addEditModalOpen: true,
      tenant: tenant,
      tenantId: id
    });
  };

  hideModal = () => {

    this.setState({
      addEditModalOpen: false,
      tenantId: undefined,
      tenant: undefined
    });
  };

  onSaveUpsertForm = () => {
    const {searchValues, cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        tenantsLoaded: true
      })
    };

    searchTenants(searchValues, cancelTokenSource)
        .then(response => {
          this.setState({
            foundTenants: response
          });
        })
        .catch(noop)
        .finally(onFinally);
    this.hideModal();
  };

  deleteTenant = (id: string): void => {
    const {cancelTokenSource} = this.state;
    deleteTenant(id, cancelTokenSource).then(() => {
      this.setState(({foundTenants}: State) => ({
        foundTenants: foundTenants.filter((tenant: TenantDto) => tenant.id != id)
      }))
    }).catch(noop);
  };

  handleChange = debounce(({values}: TenantFieldProps): void => {
    if (!this.mounted) {
      return;
    }

    const tenantSearchTerm = values.tenantSearchTerm ? values.tenantSearchTerm : "";

    const {cancelTokenSource} = this.state;
    this.setState({tenantsLoaded: false});

    const onFinally = () => {
      this.setState({
        tenantsLoaded: true
      })
    };

    searchTenants(tenantSearchTerm, cancelTokenSource)
        .then(response =>
            this.setState({
              foundTenants: response,
              searchValues: values.tenantSearchTerm ? values.tenantSearchTerm : ""
            })
        )
        .catch(noop)
        .finally(onFinally);
  }, 300);

  render(): React.ReactNode {
    const {tenantsLoaded} = this.state;
    return (
        <PraxStyledSegment>

          <div>
            {this.renderSearchTenantsSegment()}
          </div>

          <StyledViewTableRow>
            {!tenantsLoaded ? this.renderLoader() : this.renderTenantsSegment()}
          </StyledViewTableRow>

          {this.renderUpsertTenantModal()}
        </PraxStyledSegment>
    );
  }

  renderSearchTenantsSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={() => noop}
            initialValues={{}}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {foundTenants} = this.state;

    const numResults = foundTenants.length;

    return (
        <PxGrid>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("select")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="tenantSearchTerm"
                  component={Input}
                  fluid
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.showModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
        </PxGrid>
    );
  };

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("tenant.confirmDelete")}
              onConfirm={() => this.deleteTenant(cellData)}
          />
        </React.Fragment>
    );
  };

  tenantRowGetter = ({index}: any) => {
    const {foundTenants} = this.state;

    Object.assign(foundTenants[index], {index: index + 1});

    return foundTenants[index]
  };

  foundTenantsRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundTenants} = this.state;
    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(foundTenants[index].id)}
        >
          {columns}
        </div>
    );
  };

  renderTenantsSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundTenants} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundTenants.length}
            rowGetter={this.tenantRowGetter}
            rowRenderer={this.foundTenantsRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 200,
                flexGrow: 1,
                label: (getMessage("tenant.id")),
                dataKey: "id"
              },
              {
                width: 300,
                flexGrow: 2,
                label: (getMessage("tenant.dbName")),
                dataKey: "dbName"
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );
  };

  renderLoader = (): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">{getMessage("tenant.loading")}</Loader>
        </Dimmer>
    );
  };

  renderUpsertTenantModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {addEditModalOpen, tenant, tenantId} = this.state;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={this.hideModal}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("header.navigation.tenant")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertTenantForm
                tenant={tenant}
                tenantId={tenantId}
                onSave={this.onSaveUpsertForm}
                onCancel={this.hideModal}
            />
          </FormModalContentContainer>
        </Modal>
    )
  }
}

export default withMessages(TenantView);
